import { useTranslate } from '@va/localization';
import { MultiSelectFilter, TextFilter } from '@va/shared/feature-filters';
import { ApiOperators, FilterOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { useDeviceOptions, useOssOptions } from '../filter-options';
import { FilterHook } from '../types';

export const useDeviceTypeFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();
  const deviceOptions = useDeviceOptions();

  return useMemo(
    () =>
      new MultiSelectFilter({
        id: 'deviceType',
        name: 'deviceType',
        label: translate('filter.deviceType'),
        operator: FilterOperators.is,
        operatorOptions: [FilterOperators.is, FilterOperators.isNot],
        ...params,
        inputProps: { useOptions: () => ({ data: deviceOptions }), ...params?.inputProps },
      }),
    [deviceOptions, params, translate],
  );
};

export const useDisplayFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new TextFilter({
        id: 'display',
        name: 'display',
        label: translate('filter.display'),
        operator: FilterOperators.is,
        operatorOptions: [FilterOperators.is, FilterOperators.isNot],
        ...params,
        inputProps: { placeholder: translate('filters.placeholder.display'), ...params },
      }),
    [params, translate],
  );
};

export const useResolutionFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new TextFilter({
        id: 'resolution',
        name: 'resolution',
        label: translate('filter.resolution'),
        operator: FilterOperators.is,
        operatorOptions: [FilterOperators.is, FilterOperators.isNot],
        ...params,
        inputProps: { placeholder: translate('filters.placeholder.resolution'), ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const usePlatformFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new MultiSelectFilter({
        id: 'platform',
        name: 'platform',
        label: translate('filter.platform'),
        operatorOptions: [FilterOperators.is, FilterOperators.isNot],
        ...params,
        inputProps: {
          useOptions: useOssOptions,
          showSearchInput: true,
          ...params?.inputProps,
        },
      }),

    [params, translate],
  );
};

export const useUserAgentFilter: FilterHook<typeof TextFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(
    () =>
      new TextFilter({
        id: 'userAgent',
        name: 'userAgent',
        label: translate('filter.userAgent'),
        operatorOptions: [
          FilterOperators.contains,
          FilterOperators.doesNotContain,
          FilterOperators.is,
          FilterOperators.isNot,
        ],
        allowMultiSelect: true,
        operatorMapper: (operator) => {
          switch (operator) {
            case FilterOperators.contains:
              return ApiOperators.in;
            case FilterOperators.doesNotContain:
              return ApiOperators.notIn;
            case FilterOperators.is:
              return ApiOperators.eq;
            case FilterOperators.isNot:
              return ApiOperators.notEq;
            default:
              return ApiOperators.in;
          }
        },
        ...params,
        inputProps: { placeholder: translate('filters.placeholder.userAgent'), ...params?.inputProps },
      }),
    [params, translate],
  );
};
